import type { HubConnection } from '@microsoft/signalr';

import type { RealtimeGrant } from './realtime-grant.client';

export type TileUpdateHandler = (message: unknown) => void;
export type ConnectionClosedHandler = () => void;

export abstract class RealtimeConnection {
  abstract onTileUpdate(handler: TileUpdateHandler): void;
  abstract offTileUpdate(handler: TileUpdateHandler): void;
  abstract onClosed(handler: ConnectionClosedHandler): void;
  abstract start(): Promise<void>;
  abstract stop(): Promise<void>;
}

export abstract class RealtimeConnectionFactory {
  abstract create(grant: RealtimeGrant): RealtimeConnection;
}

export type SignalRLoader = () => Promise<typeof import('@microsoft/signalr')>;

export class SignalRRealtimeConnectionFactory extends RealtimeConnectionFactory {
  constructor(
    private readonly loadSignalR: SignalRLoader = () => import('@microsoft/signalr'),
  ) {
    super();
  }

  override create(grant: RealtimeGrant): RealtimeConnection {
    return new SignalRRealtimeConnection(grant.url, grant.accessToken, this.loadSignalR);
  }
}

class SignalRRealtimeConnection extends RealtimeConnection {
  private readonly tileHandlers = new Set<TileUpdateHandler>();
  private readonly closedHandlers = new Set<ConnectionClosedHandler>();
  private hub: HubConnection | null = null;
  private accessToken: string;
  private stopped = false;

  constructor(
    private readonly url: string,
    accessToken: string,
    private readonly loadSignalR: SignalRLoader,
  ) {
    super();
    this.accessToken = accessToken;
  }

  override onTileUpdate(handler: TileUpdateHandler): void {
    this.tileHandlers.add(handler);
    this.hub?.on('tileUpdate', handler);
  }

  override offTileUpdate(handler: TileUpdateHandler): void {
    this.tileHandlers.delete(handler);
    this.hub?.off('tileUpdate', handler);
  }

  override onClosed(handler: ConnectionClosedHandler): void {
    this.closedHandlers.add(handler);
  }

  override async start(): Promise<void> {
    const signalR = await this.loadSignalR();
    if (this.stopped) throw new DOMException('Connection attempt cancelled.', 'AbortError');

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(this.url, {
        accessTokenFactory: () => this.accessToken,
        transport: signalR.HttpTransportType.LongPolling,
        skipNegotiation: false,
        logMessageContent: false,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();
    for (const handler of this.tileHandlers) hub.on('tileUpdate', handler);
    hub.onclose(() => {
      for (const handler of this.closedHandlers) handler();
    });
    this.hub = hub;
    await hub.start();
  }

  override async stop(): Promise<void> {
    this.stopped = true;
    try {
      await this.hub?.stop();
    } finally {
      this.accessToken = '';
    }
  }
}